import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { BehaviorSubject, Subject, combineLatest, forkJoin, of, throwError } from 'rxjs';
import { distinctUntilChanged, filter, map, mergeMap, take, takeUntil } from 'rxjs/operators';
import { AutoDestroy } from 'src/app/decorator/auto-destroy.decorator';
import { CidaasService } from 'src/app/service/cidaas.service';
import { IPrecheckMetadata, IPreloginMetadataResponse } from 'src/app/model/suggest-verification-method.interface';
import { SharedDataService } from 'src/app/service/shared-data.service';
import { environment } from 'src/environments/environment';
import { ITenantInfo } from 'src/app/feature/verification/module/shared/model/IMfa.model';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { SharedService } from 'src/app/feature/verification/module/shared//shared.service';

export interface ILoginPrecheckPayload {
  sub?: string;
  track_id: string;
  requestId: string;
}

export interface IUiError {
  message?: string;
  default_message: string;
  status: number;
}

interface Imethod {
  name: string;
  deonticity: string;
}

interface ITenantClientData {
  tenant_name?: string;
  tenant_key?: string;
  logo_uri?: string;
  client_id?: string;
  base_url?: string;
}

interface IpreloginMeataData {
  amr_values?: string[];
  reason?: string;
  methods?: Imethod[];
  skipDurationInDays?: number;
  skipUntil?: string;
}

@Component({
  selector: 'app-suggest-verification-method',
  templateUrl: './suggest-verification-method.component.html',
  styleUrls: ['./suggest-verification-method.component.scss'],
})
export class SuggestVerificationMethodComponent implements OnInit {
  // rxjs subject to handle the component destroy
  @AutoDestroy destroy$: Subject<boolean> = new Subject<boolean>();

  preloginMetadata$ = new BehaviorSubject({});
  enrolledStatus$ = new BehaviorSubject({});
  methods$ = new BehaviorSubject<Imethod[]>([]);
  trackId$ = new BehaviorSubject<string>('');
  enrollState$ = new BehaviorSubject<string[]>([]);
  doNotAskBtn$ = new BehaviorSubject<boolean>(false);
  skipBtn$ = new BehaviorSubject<boolean>(false);
  skipUntilBtn$ = new BehaviorSubject<boolean>(false);
  continueBtn$ = new BehaviorSubject<boolean>(false);
  enrollType$ = new BehaviorSubject<string>('');
  clientTenantData$ = new BehaviorSubject<ITenantInfo>(<ITenantInfo>{});
  errorState$ = new BehaviorSubject<IUiError>(<IUiError>{});
  availableMethods = ['FIDO2', 'BACKUPCODE', 'TOTP', 'TOUCHID', 'PUSH', 'PATTERN', 'IVR', 'SMS', 'EMAIL', 'FACE'];

  /**
   * The properties of each button defined here
   */
  skipUntilProperty$ = new BehaviorSubject({
    color: 'accent',
    text: 'SUGGESTED_VERIFICATION_METHOD.ASK_AGAIN',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasSubmitBtn',
    translateArgs: {},
    style: 'secondary',
  });

  skipProperty$ = new BehaviorSubject({
    color: 'accent',
    text: 'SUGGESTED_VERIFICATION_METHOD.SKIP',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasSubmitBtn',
    translateArgs: {},
    style: 'secondary',
  });

  donotAskProperty$ = new BehaviorSubject({
    color: 'accent',
    text: 'SUGGESTED_VERIFICATION_METHOD.DONT_ASK_ANYMORE',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasSubmitBtn',
    translateArgs: {},
    style: 'secondary',
  });

  continueProperty$ = new BehaviorSubject({
    color: 'accent',
    text: 'SUGGESTED_VERIFICATION_METHOD.CONTINUE',
    alignment: 'right',
    type: 'proceed',
    underline: 'none',
    for: 'cidaasSubmitBtn',
    translateArgs: {},
    style: '',
  });

  // // dont remove the commented code which is used to mock the data
  sampleData: IPreloginMetadataResponse = {
    logged_in: false,
    validation_type: 'mfa_required',
    meta_data: {
      amr_values: ['10'],
      reason: 'ATLEASTONE',
      methods: [
        { name: 'TOTP', deonticity: 'MANDATORY' },
        { name: 'IVR', deonticity: 'OPTIONAL' },
        { name: 'FIDO2', deonticity: 'OPTIONAL' },
        { name: 'SMS', deonticity: 'OPTIONAL' },
        { name: 'EMAIL', deonticity: 'OPTIONAL' },
      ],
      skipDurationInDays: 2,
      skipUntil: '2023-10-23T05:28:42.330Z',
    },
    used: false,
  };

  constructor(
    private _cidaas: CidaasService,
    private _route: ActivatedRoute,
    private _dataService: SharedDataService,
    private translate: TranslateService,
    private _sharedService: SharedService
  ) { }

  // onInit
  ngOnInit(): void {
    // set the translation language
    this._setTranslationLang();

    // getting clientinfo and tenantInfo for QR code generation
    combineLatest([this._dataService._getClientInfo(), this._cidaas.getTenantInfo()])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([client, tenant]) => {
        let data: ITenantInfo = {
          tenant_name: tenant?.tenant_name,
          tenant_key: tenant?.tenant_key,
          logo_uri: tenant?.logo_uri || '',
          client_id: client?.client_id,
          base_url: environment?.base_url,
        };
        this.clientTenantData$.next(data);
      });

    this._route.queryParams
      .pipe(
        takeUntil(this.destroy$),
        map((param) => ({ track_id: param.track_id, requestId: param.requestId } || {})),
        distinctUntilChanged(),
        take(1)
      )
      .subscribe((q) => {
        if (q.requestId && q.track_id) {
          this.trackId$.next(q.track_id);
          const options = { requestId: q.requestId, trackId: q.track_id };
          this._cidaas
            ._getMissingFieds$('login', options)
            .pipe(
              mergeMap((res) => {
                return !res || !res?.meta_data
                  ? throwError({message:`ERROR_CODES.SUGGEST_VERIFICATION.USER_ACTION.${res.code}`, default_message:`ERROR_CODES.SUGGEST_VERIFICATION.USER_ACTION.DEFAULT`, status:res.status})
                  : of(res?.meta_data);
              }),
              take(1)
            )
            .subscribe(
              (preloginMetadata) => {
                /**
                 * will filter the availableMethods which are already implemented and return the methods.
                 * And need to make sure that while implementing new methods we have to add those into the
                 * availableMethods[].
                 */
                if (preloginMetadata.methods) {
                  this.methods$.next(
                    preloginMetadata.methods.filter((x) => {
                      return this.availableMethods.includes(x?.name);
                    })
                  );
                }
                this.preloginMetadata$.next(preloginMetadata);
                /**
                 * will handle the buttons in the below method
                 */
                this._enableButtonsOnUserInteraction(preloginMetadata);
              },
              (err) => {
                this.errorState$.next({message:`ERROR_CODES.SUGGEST_VERIFICATION.PRELOGIN.${err.code}`, default_message:`ERROR_CODES.SUGGEST_VERIFICATION.PRELOGIN.DEFAULT`, status: err.status})
              }
            );
        } else {
          const queryParams: Params = {
            error_code: 'DHP_SVM_35002',
            error_hint: 'MISSING_QUERY_PARAMS',
            error_description: "Due to missing query params, We couldn't process the request right now.",
          };
          this._cidaas.navigateToErrorPage(queryParams);
        }
      });
  }

  /**
   *
   * @param precheckMetadata
   * Should meet the below criteria to enable the skipuntil button
   * if there is a skipDurationInDays available
   * if there are methods methods available
   *
   * if the above criterias are not matching will enable the skip button
   */
  _enableSkipUntilBtn = (
    skipDurationInDays: number | undefined,
    skipUntil: string | undefined,
    isSecondaryBtn: boolean,
    reason?: string
  ): void => {
    if (skipDurationInDays && skipUntil && new Date(skipUntil).getTime() > new Date().getTime()) {
      // settingup the askagain button properties
      this.skipUntilProperty$.next({
        color: 'accent',
        text: 'SUGGESTED_VERIFICATION_METHOD.ASK_AGAIN',
        alignment: 'right',
        type: 'proceed',
        underline: 'none',
        for: 'cidaasSubmitBtn',
        translateArgs: { count: skipDurationInDays },
        style: isSecondaryBtn ? 'secondary' : '',
      });

      // check for the available methods
      this.methods$.subscribe((m) => {
        if (m.length) { this.skipUntilBtn$.next(true) };
      });
    } else {
      // this.skipUntilBtn$.next(false);
      // settingup the askagain button properties
      this.skipUntilProperty$.next({
        color: 'accent',
        text: 'SUGGESTED_VERIFICATION_METHOD.ASK_AGAIN',
        alignment: 'right',
        type: 'proceed',
        underline: 'none',
        for: 'cidaasSubmitBtn',
        translateArgs: { count: skipDurationInDays },
        style: '',
      });
      this.skipProperty$.next({
        color: 'accent',
        text: 'SUGGESTED_VERIFICATION_METHOD.SKIP',
        alignment: 'right',
        type: 'proceed',
        underline: 'none',
        for: 'cidaasSubmitBtn',
        translateArgs: {},
        style: '',
      });

      if ((skipUntil && new Date(skipUntil).getTime() > new Date().getTime()) || reason === 'NONE') {
        if (skipDurationInDays) { this.skipUntilBtn$.next(true) };
      } else {
        this.skipBtn$.next(false);
        this.skipUntilBtn$.next(false);
      }
    }
  };

  /**
   *
   * @param precheckMetadata
   * will enable the donotAsk button
   */
  _enableDoNotAskBtn = (methods: Imethod[]): void => {
    // enables the donotAsk button if all the methods are optional
    if (methods.length) {
      methods?.filter((m) => {
        return m.deonticity != 'OPTIONAL';
      }).length == 0
        ? this.doNotAskBtn$.next(true)
        : this.doNotAskBtn$.next(false);
    } else {
      this.doNotAskBtn$.next(false);
    }
  };

  /**
   * will enables the continue button
   */
  _enableContinueBtn = (): void => {
    this.enrollState$
      .pipe(
        distinctUntilChanged((p, c) => {
          return p.length != c.length;
        })
      )
      .subscribe((enrolledMethods: string[]) => {
        if (enrolledMethods.length > 0) {
          this.continueBtn$.next(true);
        }
      });
  };

  /**
   *
   * @param type
   * will add given method to the enrollment que
   */
  _enrollMethod = (type: string) => {
    // will push the method into the enrollment que
    this.enrollType$.next(type);
  };

  /**
   *
   * @param enrollmentStatus
   * will execute once we get an enrollment status from the verification module
   * if the status enrolled is true will invoke the _completeEnrollMethod
   * else it wont do anything
   */
  _checkEnrollMethodStatus = (enrolmentStatus: any) => {
    this.enrollType$.next('');
    if (enrolmentStatus.enrolled) {
      this.enrolledStatus$.next(enrolmentStatus?.enrolmentStatus);
      this._completeEnrollMethod(enrolmentStatus.type);
    }
  };

  /**
   *
   * @param method
   * will execute this function once the method enrollment is done
   */
  _completeEnrollMethod = (method: string) => {
    // will add the method into the enrolled methods array
    let m = this.enrollState$.getValue();
    m.push(method);
    this.enrollState$.next(m);
    this.enrollType$.next('');
    // will remove the enrolled method from the existing observable array
    this.methods$.next(
      this.methods$.getValue().filter((x) => {
        return x.name != method.toUpperCase();
      })
    );
  };

  _loginPrecheck(): void {
    this.enrolledStatus$.subscribe((status) => {
      if (status && status['sub']) {
        let payload: ILoginPrecheckPayload = {} as ILoginPrecheckPayload;

        // payload.status_id = status?.status_id;
        payload.sub = status['sub'];
        payload.track_id = this._route.snapshot.queryParams.track_id;
        payload.requestId = this._route.snapshot.queryParams.requestId;

        // call the precheck continue
        this._cidaas.mfaContinue(payload);
      } else {
        console.error('[Error]: there is missing information in enrolled status');
      }
    });
  }

  /**
   * Will set the translation language which is resolving in the router.
   */
  _setTranslationLang = () => {
    this._route.data.subscribe((data) => {
      this.translate.use(data.currentLang);
    });
  };

  /**
   *
   * @param action string
   *
   */
  _userAction = (action: string) => {
    this._sharedService._userActionOnEnrolment(this.trackId$.getValue(), action).subscribe(
      (res) => {
        let payload: ILoginPrecheckPayload = {} as ILoginPrecheckPayload;
        payload.track_id = this._route.snapshot.queryParams.track_id;
        payload.requestId = this._route.snapshot.queryParams.requestId;
        // call the precheck continue
        this._cidaas.mfaContinue(payload);
      },
      (err) => {
        this.errorState$.next(err);
      }
    );
  };

  /**
   *
   * @param preloginData
   * @param methods
   */
  _enableButtonsForOptionalMethods = (preloginData: IpreloginMeataData, methods: Imethod[]) => {
    const methodCount = methods.length;
    if (methodCount > 0) {
      const optionalMethodsCount = methods.filter((method: Imethod) => method.deonticity == 'OPTIONAL').length;
      if (optionalMethodsCount === methods.length && preloginData.reason !== 'ALLOFMANDATORY') {
        this._enableDoNotAskBtn(methods);
        this._enableSkipUntilBtn(preloginData.skipDurationInDays, preloginData.skipUntil, false, preloginData.reason);
      } else {
        // will check whether the due date is in future
        if (new Date(preloginData?.skipUntil || '').getTime() > new Date().getTime()) {
          // will enable donotask button if all methods are optional
          if (optionalMethodsCount === methods.length) {
            this._enableDoNotAskBtn(methods);
          }
          // will enable the askagain button
          this._enableSkipUntilBtn(preloginData.skipDurationInDays, preloginData.skipUntil, false, preloginData.reason);
        } else {
          // if the due date is in the past will enable only the continue button
          if (
            !methods.filter((x) => {
              return x.deonticity == 'MANDATORY';
            }).length
          ) {
            this._enableContinueBtn();
          }
        }
      }
    } else {
      // disabling all other buttons except continue
      this.skipBtn$.next(false);
      this.skipUntilBtn$.next(false);
      this.doNotAskBtn$.next(false);
      this._enableContinueBtn();
    }
  };

  /**
   *
   * @param preloginData
   */
  _enableButtonsOnUserInteraction = (preloginData: IpreloginMeataData): void => {
    // will handle the case 1 scenario
    if (preloginData && preloginData.reason == 'ATLEASTONE') {
      this._enableContinueBtn();
    } else {
      this.methods$.subscribe((methods: Imethod[]) => {
        // will enable buttons on user actions
        this._enableButtonsForOptionalMethods(preloginData, methods);
      });
    }
  };
}
